// Utility for tracking
const trackEvent = (action, category, label) => {
  if (window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
    });
  }
};

import { useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  MessageCircle, 
  Menu, 
  X, 
  Flame,
  Wrench,
  ShieldCheck,
  FileText,
  Gauge,
  Settings,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  MapPin,
  Mail,
  Clock,
  ArrowUp,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

// Novas seções importadas
import { Process } from './components/Process';
import { Differentials } from './components/Differentials';
import { FAQ } from './components/FAQ';

// Coloque a logo na pasta public/ e use: "/logo.png"
const LOGO_URL = "/logo.png";
const BRAND_ICON_URL = "/brand/prf-icon.png";

// Loading Animation
const LoadingAnimation = ({ onComplete }) => {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0A0A0A]"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      onAnimationComplete={() => setTimeout(onComplete, 2500)}
      data-testid="loading-animation"
    >
      <motion.div
        className="flex items-center gap-4 mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src={BRAND_ICON_URL}
          alt="PRF Tubulações de Gás"
          className="h-16 md:h-20 w-auto object-contain"
        />
        <span className="text-white font-bold text-xl md:text-2xl">PRF Tubulações de Gás</span>
      </motion.div>
      
      {/* Animated Flame */}
      <motion.div
        className="relative mb-8"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-16 h-16 bg-[#FF6B00] rounded-full blur-xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />
        <Flame className="w-12 h-12 text-[#FF6B00] relative z-10" />
      </motion.div>

      {/* Progress Bar */}
      <div className="w-64 h-1 bg-[#2A2A2A] overflow-hidden">
        <motion.div
          className="h-full bg-[#FF6B00]"
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
      </div>
      
      <motion.p
        className="mt-4 text-[#666] text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        Carregando...
      </motion.p>
    </motion.div>
  );
};

// WhatsApp Float Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/5521990323790?text=Olá! Gostaria de solicitar um orçamento."
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-24 right-6 z-40 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20BA5C] hover:scale-110 transition-all duration-300 group "
    data-testid="whatsapp-button"
    onClick={() => trackEvent("whatsapp_click", "Engagement", "Floating Button")}
    aria-label="Fale pelo WhatsApp"
  >
    <MessageCircle className="w-7 h-7" />
    <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg pointer-events-none">
      Fale pelo WhatsApp
    </span>
  </a>
);

// Header
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Início', href: '#inicio' },
    { name: 'Serviços', href: '#servicos' },
    { name: 'Processo', href: '#processo' },
    { name: 'Diferenciais', href: '#diferenciais' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Contato', href: '#contato' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#222]" data-testid="header">
      {/* Top Bar */}
      <div className="bg-[#FF6B00] py-2">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-4 text-black font-medium">
            <a href="tel:+5521990323790" className="flex items-center gap-2 hover:underline">
              <Phone size={14} />
              <span>(21) 99032-3790</span>
            </a>
            <a href="https://wa.me/5521990323790" className="flex items-center gap-2 hover:underline">
              <MessageCircle size={14} />
              <span>(21) 99032-3790</span>
            </a>
          </div>
          <span className="text-black font-medium">Atendimento de Seg a Sex - 08h às 17h</span>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Brand - Ícone + Texto */}
          <a 
            href="#inicio" 
            className="flex items-center gap-3 flex-shrink-0" 
            data-testid="logo-link"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img 
              src={BRAND_ICON_URL} 
              alt="PRF Tubulações de Gás" 
              className="h-9 md:h-11 w-auto object-contain" 
            />
            <span className="text-white font-bold text-base md:text-lg whitespace-nowrap">
              PRF Tubulações de Gás
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[#EDEDED] hover:text-[#FF6B00] transition-colors font-medium"
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://wa.me/5521990323790"
              className="bg-[#FF6B00] text-black font-bold px-6 py-3 hover:bg-[#E65A00] transition-colors flex items-center gap-2"
              data-testid="header-whatsapp"
              onClick={() => trackEvent("whatsapp_click", "Engagement", "Header Button")}
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[#EDEDED] p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-[#222] pt-4" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block py-3 text-[#EDEDED] hover:text-[#FF6B00] transition-colors font-medium"
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://wa.me/5521990323790"
              className="mt-4 bg-[#FF6B00] text-black font-bold px-6 py-3 hover:bg-[#E65A00] transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section id="inicio" className="relative min-h-[60vh] md:min-h-[85vh] lg:min-h-[90vh] overflow-hidden" data-testid="hero-section">
      {/* Video Background - Only in Hero */}
      <BackgroundVideo />
      
      {/* Content */}
      <div className="relative z-10 pt-36 pb-16 md:pt-44 md:pb-20 min-h-[60vh] md:min-h-[85vh] lg:min-h-[90vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block bg-[#FF6B00]/20 text-[#FF6B00] px-4 py-2 text-sm font-medium mb-6 border border-[#FF6B00]/40 backdrop-blur-sm">
                  Serviços de Gás no Rio de Janeiro
                </span>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                  Serviços de{' '}
                  <span className="text-[#FF6B00]">Gás Encanado</span>
                </h1>
                
                <p className="text-lg text-white/90 mb-8 leading-relaxed drop-shadow-md max-w-xl">
                  Instalação, manutenção e assistência técnica em tubulações de gás. 
                  Atendemos residências, condomínios e empresas no Rio de Janeiro.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://wa.me/5521990323790"
                    className="bg-[#25D366] text-white font-bold px-8 py-4 hover:bg-[#20BA5C] transition-colors flex items-center justify-center gap-3 shadow-lg"
                    data-testid="hero-whatsapp"
                    onClick={() => trackEvent("whatsapp_click", "Engagement", "Hero Button")}
                  >
                    <MessageCircle size={20} />
                    Fale pelo WhatsApp
                  </a>
                  <a
                    href="tel:+5521990323790"
                    className="bg-white/10 backdrop-blur-sm border-2 border-[#FF6B00] text-[#FF6B00] font-bold px-8 py-4 hover:bg-[#FF6B00] hover:text-black transition-colors flex items-center justify-center gap-3"
                    data-testid="hero-phone"
                    onClick={() => trackEvent("phone_click", "Engagement", "Hero Phone")}
                  >
                    <Phone size={20} />
                    (21) 99032-3790
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Emergency Box */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#1A1A1A]/90 backdrop-blur-sm border border-[#333] p-8 shadow-2xl"
              data-testid="emergency-box"
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-8 h-8 text-[#FF6B00]" />
                <h2 className="text-2xl font-bold text-white">Está precisando de urgência?</h2>
              </div>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Seu medidor foi lacrado?',
                  'Está sentindo cheiro de gás?',
                  'Consumo acima do normal?',
                  'Vazamento de gás?'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#CCC]">
                    <ChevronRight className="w-5 h-5 text-[#FF6B00]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://wa.me/5521990323790"
                className="w-full bg-[#FF6B00] text-black font-bold py-4 hover:bg-[#E65A00] transition-colors flex items-center justify-center gap-3"
                data-testid="emergency-whatsapp"
                onClick={() => trackEvent("whatsapp_click", "Engagement", "Emergency Box")}
              >
                <MessageCircle size={20} />
                Atendimento WhatsApp
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Wrench,
      title: 'Instalação de Tubulação de Gás',
      description: 'Instalação completa de tubulação de gás para residências, apartamentos e comércios, seguindo normas técnicas de segurança.',
      image: 'https://images.pexels.com/photos/3721272/pexels-photo-3721272.jpeg',
    },
    {
      icon: Settings,
      title: 'Manutenção de Fogão e Aquecedor',
      description: 'Instalação, manutenção e reparo de fogões e aquecedores a gás. Limpeza, troca de peças e regulagem de chama.',
      image: 'https://images.unsplash.com/photo-1767771665869-51fab4d8c83b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxnYXMlMjBzdG92ZSUyMGZsYW1lJTIwYnVybmVyJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzExMzUxMjd8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Gauge,
      title: 'Teste de Estanqueidade',
      description: 'Teste para verificar vazamentos na tubulação de gás. Identificação e correção de problemas com emissão de laudo.',
      image: 'https://images.unsplash.com/photo-1600065621653-2f1de87d0f43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHx5ZWxsb3clMjBnYXMlMjBwaXBlcyUyMHZhbHZlJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzExMzI3MzZ8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: RefreshCw,
      title: 'Conversão GLP / Gás Natural',
      description: 'Conversão de equipamentos de gás GLP para gás natural e vice-versa. Adaptação de fogões e aquecedores.',
      image: 'https://images.unsplash.com/photo-1565191262855-2e6c531f3867?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHx5ZWxsb3clMjBnYXMlMjBwaXBlcyUyMHZhbHZlJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzExMzI3MzZ8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: ShieldCheck,
      title: 'Adequação e Regularização',
      description: 'Adequação de instalações às normas da concessionária. Regularização para deslacramento do medidor de gás.',
      image: 'https://images.unsplash.com/photo-1693900720203-4e7b905d7986?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHx5ZWxsb3clMjBnYXMlMjBwaXBlcyUyMHZhbHZlJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzExMzI3MzZ8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: FileText,
      title: 'Projeto e Consultoria',
      description: 'Elaboração de projetos técnicos, memorial descritivo e ART. Consultoria para instalações de gás.',
      image: 'https://images.unsplash.com/photo-1760886810583-dbb87686d08c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHx3YXRlciUyMGhlYXRlciUyMGdhcyUyMGJvaWxlciUyMGhvbWV8ZW58MHx8fHwxNzcxMTM1MTMyfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Flame,
      title: 'Aplicação de Resina',
      description: 'Oferecemos o serviço de aplicação de resina para diversas superfícies, garantindo durabilidade e um acabamento impecável. Realizamos o serviço em qualquer andar.',
      image: '/services/resina.png',
      promo: true,
      price: 'R$ 560,00',
      installments: 'em até 10x no cartão',
    },
  ];

  return (
    <section id="servicos" className="py-20 bg-[#0F0F0F]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-[#FF6B00] font-medium mb-4 block">NOSSOS SERVIÇOS</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Serviços de Gás Encanado
          </h2>
          <p className="text-[#999] max-w-2xl mx-auto">
            Oferecemos soluções completas em instalação e manutenção de gás. 
            Atendemos residências, condomínios e empresas.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`bg-[#1A1A1A] border overflow-hidden group transition-colors relative ${
                service.promo 
                  ? 'border-[#FF6B00] ring-2 ring-[#FF6B00]/30' 
                  : 'border-[#333] hover:border-[#FF6B00]'
              }`}
              data-testid={`service-card-${index}`}
            >
              {/* Badge Promoção */}
              {service.promo && (
                <div className="absolute top-4 right-4 z-10 bg-[#FF6B00] text-black font-bold px-3 py-1.5 text-xs uppercase tracking-wide  shadow-lg">
                  PROMOÇÃO
                </div>
              )}
              
              <div className="h-48 overflow-hidden relative">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {service.promo && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B00]/20 to-transparent" />
                )}
              </div>
              <div className="p-6">
                <div className="w-12 h-12 bg-[#FF6B00]/10 border border-[#FF6B00]/30 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-[#FF6B00]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-[#999] text-sm leading-relaxed mb-4">{service.description}</p>
                
                {/* Preço e Parcelas */}
                {service.promo && service.price && (
                  <div className="bg-[#FF6B00]/10 border border-[#FF6B00]/30 p-4 mb-4 rounded">
                    <div className="text-2xl font-bold text-[#FF6B00]">{service.price}</div>
                    <div className="text-sm text-[#CCC]">{service.installments}</div>
                  </div>
                )}
                
                <a
                  href="https://wa.me/5521990323790"
                  className={`inline-flex items-center gap-2 font-medium hover:gap-3 transition-all ${
                    service.promo 
                      ? 'bg-[#FF6B00] text-black px-4 py-2 hover:bg-[#E65A00]' 
                      : 'text-[#FF6B00]'
                  }`}
                >
                  {service.promo ? 'Aproveitar oferta' : 'Solicitar orçamento'} <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => (
  <section id="sobre" className="py-20 bg-[#0A0A0A]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[#FF6B00] font-medium mb-4 block">SOBRE NÓS</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            PRF Tubulações de Gás
          </h2>
          <p className="text-[#999] mb-6 leading-relaxed">
            A PRF Tubulações de Gás é especializada em serviços de instalação e manutenção 
            de sistemas de gás encanado no Rio de Janeiro. Nossa equipe técnica qualificada 
            garante segurança e qualidade em todos os serviços.
          </p>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
              'Equipe técnica qualificada',
              'Atendimento rápido',
              'Garantia nos serviços',
              'Preços competitivos',
              'Normas de segurança',
              'Laudos e certificados'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#CCC]">
                <CheckCircle2 className="w-5 h-5 text-[#FF6B00] flex-shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>

          <a
            href="https://wa.me/5521990323790"
            className="inline-flex items-center gap-3 bg-[#FF6B00] text-black font-bold px-8 py-4 hover:bg-[#E65A00] transition-colors"
            data-testid="about-cta"
          >
            <MessageCircle size={20} />
            Fale pelo WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <img
            src="https://images.unsplash.com/photo-1764677367365-d56787922d83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxnYXMlMjBzdG92ZSUyMGZsYW1lJTIwYnVybmVyJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzExMzUxMjd8MA&ixlib=rb-4.1.0&q=85"
            alt="Chama de gás"
            className="w-full h-[400px] object-cover"
          />
          <div className="absolute -bottom-6 -left-6 bg-[#FF6B00] p-6">
            <div className="text-4xl font-bold text-black">+10</div>
            <div className="text-sm text-black font-medium">Anos de Experiência</div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Contact Section
const Contact = () => {
  return (
    <section id="contato" className="py-20 bg-[#0F0F0F]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-[#FF6B00] font-medium mb-4 block">CONTATO</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Fale Conosco
          </h2>
          <p className="text-[#999] max-w-2xl mx-auto">
            Entre em contato pelo WhatsApp ou telefone para solicitar um orçamento.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {[
            { icon: MessageCircle, title: 'WhatsApp', value: '(21) 99032-3790', link: 'https://wa.me/5521990323790', highlight: true },
            { icon: Phone, title: 'Telefone', value: '(21) 99032-3790', link: 'tel:+5521990323790', highlight: false },
            { icon: Mail, title: 'E-mail', value: 'atendimento@prfgas.com', link: 'mailto:atendimento@prfgas.com', highlight: false },
            { icon: MapPin, title: 'Endereço', value: 'Av. Maracanã, 39 - Tijuca, Rio de Janeiro - RJ', link: 'https://maps.google.com/?q=Av.+Maracanã,+39+-+Tijuca,+Rio+de+Janeiro+-+RJ', highlight: false },
            { icon: Clock, title: 'Horário', value: 'Seg a Sex: 08h às 17h', link: null, highlight: false },
          ].map((item, i) => (
            <div 
              key={i} 
              className={`p-6 flex items-start gap-4 ${item.highlight ? 'bg-[#25D366] border-[#25D366]' : 'bg-[#1A1A1A] border-[#333]'} border`}
              data-testid={`contact-info-${i}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${item.highlight ? 'bg-white/20' : 'bg-[#FF6B00]/10 border border-[#FF6B00]/30'}`}>
                <item.icon className={`w-6 h-6 ${item.highlight ? 'text-white' : 'text-[#FF6B00]'}`} />
              </div>
              <div>
                <div className={`text-sm mb-1 ${item.highlight ? 'text-white/80' : 'text-[#999]'}`}>{item.title}</div>
                {item.link ? (
                  <a 
                    href={item.link} 
                    target={item.link.startsWith('http') ? '_blank' : undefined} 
                    rel="noopener noreferrer" 
                    className={`font-medium hover:underline ${item.highlight ? 'text-white text-lg' : 'text-white hover:text-[#FF6B00]'} transition-colors`}
                  >
                    {item.value}
                  </a>
                ) : (
                  <span className={`${item.highlight ? 'text-white' : 'text-white'}`}>{item.value}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA WhatsApp */}
        <div className="text-center">
          <a
            href="https://wa.me/5521990323790"
            className="inline-flex items-center gap-3 bg-[#25D366] text-white font-bold px-10 py-5 text-lg hover:bg-[#20BA5C] transition-colors"
            data-testid="contact-whatsapp-cta"
            onClick={() => trackEvent("whatsapp_click", "Engagement", "Contact Page CTA")}
          >
            <MessageCircle size={24} />
            Chamar no WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-[#222] py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div className="md:col-span-2">
          <a href="#inicio" className="flex items-center gap-3 mb-4">
            <img src={BRAND_ICON_URL} alt="PRF Tubulações de Gás" className="h-10 w-auto object-contain" />
            <span className="text-white font-bold text-lg">PRF Tubulações de Gás</span>
          </a>
          <p className="text-[#999] text-sm mb-4">
            Segurança, precisão e excelência em tubulações de gás.<br />
            Atendemos residências, condomínios e empresas no Rio de Janeiro.
          </p>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Serviços</h4>
          <ul className="space-y-2 text-sm text-[#999]">
            <li>Instalação de Tubulação</li>
            <li>Manutenção de Equipamentos</li>
            <li>Teste de Estanqueidade</li>
            <li>Conversão de Gás</li>
            <li>Regularização</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Contato</h4>
          <ul className="space-y-2 text-sm text-[#999]">
            <li className="flex items-center gap-2"><Phone size={14} className="text-[#FF6B00]" /> (21) 99032-3790</li>
            <li className="flex items-center gap-2"><MessageCircle size={14} className="text-[#FF6B00]" /> (21) 99032-3790</li>
            <li className="flex items-center gap-2"><Mail size={14} className="text-[#FF6B00]" /> atendimento@prfgas.com</li>
            <li className="flex items-center gap-2"><MapPin size={14} className="text-[#FF6B00]" /> Tijuca, Rio de Janeiro - RJ</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#222] pt-8 text-center text-sm text-[#666]">
        © {new Date().getFullYear()} PRF Tubulações de Gás. Todos os direitos reservados.
      </div>
    </div>

    {/* Scroll to Top */}
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 left-6 bg-[#FF6B00] text-black p-3 hover:bg-[#E65A00] hover:scale-110 transition-all duration-300 z-30 shadow-lg"
      data-testid="scroll-to-top"
      aria-label="Voltar ao topo"
    >
      <ArrowUp size={20} />
    </button>
  </footer>
);

// Background Video Component - Only for Hero
const BackgroundVideo = () => (
  <div className="absolute inset-0 w-full h-full overflow-hidden">
    <video
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 w-full h-full object-cover"
    >
      <source src="/videos/bg-video.mp4" type="video/mp4" />
    </video>
    {/* Dark Overlay */}
    <div className="absolute inset-0 bg-black/55" />
  </div>
);

// Main App
function App() {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <AnimatePresence>
        {isLoading && <LoadingAnimation onComplete={() => setIsLoading(false)} />}
      </AnimatePresence>
      
      {!isLoading && (
        <>
          <Header />
          <main>
            <Hero />
            <Services />
            <Process />
            <Differentials />
            <FAQ />
            <Contact />
          </main>
          <Footer />
          <WhatsAppButton />
        </>
      )}
    </div>
  );
}

export default App;
